import React, { ReactElement } from 'react';
import useStyles from './styles';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from 'components/atoms/translated_text';
import { Translations } from './configuration';
import {
  AppDatePicker,
  AppSelect,
  AppTextField,
} from 'components/molecules/form_controls';
import GridLayout from 'components/molecules/grid_layout';
import { getState } from 'redux/store';
import { ReducerKeys } from 'redux/reducers';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { DateInMonthsRange } from 'utils/dates';
import VatReturnsListActions from 'redux/reducers/vatReturn_list_reducer/actions';
import * as VatReturnListSelectors from 'redux/reducers/vatReturn_list_reducer/selectors';
import { StateStatus } from 'redux/utils/common';
import { Roles, SchemeTypes } from 'config';
import * as VatReturnService from 'services/api/vatreturn_list';
import * as DateUtils from 'utils/dates';
interface FormFields {
  type: string | null;
  vatNum: string | null;
  period: string | null;
  year: string | null;
  startDate: Date | null;
  endDate: Date | null;
}

interface IElementsProps {
  size: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  element: ReactElement;
  id: string;
  rowStyle?: any;
}

interface IProps {
  role: Roles;
  inputVatNumber?: string;
}

export default function AddVatReturnForm({
  role,
  inputVatNumber,
}: IProps): ReactElement {
  // console.log('Render AddVatReturnForm');
  const dispatch = useDispatch();
  const defaultValues: FormFields = {
    type: null,
    vatNum: null,
    period: null,
    year: null,
    startDate: null,
    endDate: null,
  };

  const { errors, control, handleSubmit, watch, reset, setValue, setError } =
    useForm<FormFields>({
      defaultValues,
    });
  const vatNumber = watch('vatNum');
  const classes = useStyles();

  const activeRegistrations =
    VatReturnListSelectors.useGetUserInfoForVatReturnCreation();
  const agentTraders = VatReturnListSelectors.useGetAgentTraders();

  React.useEffect(() => {
    if (role !== Roles.Agent)
      dispatch(VatReturnsListActions.fetchUserInfoAction({}));
    else dispatch(VatReturnsListActions.fetchAgentTradersAction());
  }, [dispatch]);

  React.useEffect(() => {
    if (
      vatNumber &&
      role === Roles.Agent &&
      agentTraders.status === StateStatus.Success
    ) {
      var applicant_id = null;
      for (let i = 0; i < agentTraders.data.length; i++) {
        if (agentTraders.data[i].vat_id_num === vatNumber) {
          applicant_id = agentTraders.data[i].applicant_id;
          break;
        }
      }
      dispatch(
        VatReturnsListActions.fetchUserInfoAction({
          applicant_id: `${applicant_id}`,
        })
      );
    }
  }, [vatNumber, agentTraders]);

  React.useEffect(() => {
    if (
      role !== Roles.Agent &&
      activeRegistrations.status === StateStatus.Success
    ) {
      reset({
        vatNum: activeRegistrations.data.vat_id_num,
        type: `${activeRegistrations.data.scheme_type}`,
        period: null,
        year: null,
        startDate: null,
        endDate: null,
      });
    } else {
      setValue('type', `${SchemeTypes.IOSS}`);
    }
  }, [activeRegistrations]);

  React.useEffect(() => {
    if (
      role === Roles.Agent &&
      agentTraders &&
      agentTraders.status === StateStatus.Success &&
      inputVatNumber
    )
      setValue('vatNum', inputVatNumber);
  }, [agentTraders]);

  const onSubmit = (data: any) => {
    if (
      (data.startDate || data.endDate) &&
      !hasSelectedPeriodEnded(data.period, data.year)
    ) {
      setError('endDate', {
        type: 'manual',
        message: 'The selected period has not ended yet.',
      });
      return;
    }

    const startDate = new Date(data.startDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(data.endDate);
    endDate.setHours(0, 0, 0, 0);
    if (
      activeRegistrations &&
      activeRegistrations.status === StateStatus.Success &&
      activeRegistrations.data.active_registrations.length > 0
    ) {
      var user_id = null;
      if (role === Roles.Agent) {
        for (let i = 0; i < agentTraders.data.length; i++) {
          if (agentTraders.data[i].vat_id_num === vatNumber) {
            user_id = agentTraders.data[i].user_id;
            break;
          }
        }
      }
      dispatch(
        VatReturnsListActions.addVatReturnAction({
          user_id: user_id ?? undefined,
          quarter: data.period,
          year: data.year,
          start_date: data.startDate
            ? DateUtils.convertToUtc(data.startDate)
            : data.startDate,
          end_date: data.endDate
            ? DateUtils.convertToUtc(data.endDate)
            : data.endDate,
        })
      );
    }
  };

  const gridElements = (): IElementsProps[] => {
    let activeRegistrationNotes: IElementsProps = {
      element: renderActiveRegistrationError(),
      size: 12,
      id: 'activeRegistrationNote',
    };
    return [
      {
        element: renderTypeField(errors, control),
        size: 12,
        id: 'type',
      },
      ...(role === Roles.Agent
        ? ([
            {
              element: renderTradersList(errors, control, agentTraders.data),
              size: 12,
              id: 'vatNum',
            },
          ] as any)
        : ([
            {
              element: renderVatNumberField(errors, control),
              size: 12,
              id: 'vatNum',
            },
          ] as any)),
      {
        element: renderPeriodField(
          errors,
          control,
          activeRegistrations?.data?.scheme_type
        ),
        size: 12,
        id: 'period',
      },
      {
        element: renderYearField(errors, control),
        size: 12,
        id: 'year',
      },
      {
        element: renderNotes(),
        size: 12,
        id: 'notes',
      },
      {
        element: renderStartDateField(
          errors,
          control,
          watch('startDate'),
          watch('endDate'),
          watch('year'),
          watch('period')
        ),
        size: 12,
        id: 'startDate',
      },
      {
        element: renderEndDateField(
          errors,
          control,
          watch('startDate'),
          watch('endDate'),
          watch('year'),
          watch('period')
        ),
        size: 12,
        id: 'endDate',
      },
      ...(activeRegistrations &&
      activeRegistrations.status === StateStatus.Success &&
      activeRegistrations.data.active_registrations.length === 0
        ? [activeRegistrationNotes]
        : []),
      {
        element: (
          <Button
            variant="contained"
            color="primary"
            type="submit"
            style={{ width: '100%' }}
            disabled={
              activeRegistrations &&
              (activeRegistrations.status === StateStatus.Pending ||
                (activeRegistrations.status === StateStatus.Success &&
                  activeRegistrations.data.active_registrations.length === 0))
            }
          >
            <TranslatedText textMap={Translations.add} />
          </Button>
        ),
        size: 12,
        id: 'action',
      },
    ];
  };

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit(onSubmit)}>
      <GridLayout elements={gridElements()} />
    </form>
  );
}

const renderTypeField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  const schemeTypes = VatReturnListSelectors.useGetSchemeTypes();
  let types_options = [];
  types_options.push({
    id: null,
    label: Translations.type_control.English,
  });
  if (schemeTypes && schemeTypes.status === StateStatus.Success)
    for (let type of schemeTypes.data)
      types_options.push({
        id: type.applicant_type_id,
        label: type.description,
      });
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="type"
      label={<TranslatedText textMap={Translations.type_control} />}
      error={'type' in errors}
      name="type"
      control={control}
      rules={{ required: 'Required Field' }}
      options={types_options as any}
      helperText={errors.type && errors.type.message}
      disabled={true}
    />
  );
};

const renderVatNumberField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="vatNum"
      label={<TranslatedText textMap={Translations.vat_num_control} />}
      error={'vatNum' in errors}
      name="vatNum"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={errors.vatNum && errors.vatNum.message}
      disabled={true}
    />
  );
};

const renderTradersList = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  agentTraders: Array<VatReturnService.ITradersInfo>
) => {
  const traders: Array<any> = [];
  if (agentTraders) {
    for (let trader of agentTraders) {
      traders.push({
        id: trader.vat_id_num,
        label: trader.vat_id_num,
      });
    }
  }
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="vatNum"
      label={<TranslatedText textMap={Translations.vat_num_control} />}
      error={'vatNum' in errors}
      name="vatNum"
      control={control}
      rules={{ required: 'Required Field' }}
      options={traders as any}
      helperText={errors.vatNum && errors.vatNum.message}
    />
  );
};

const renderPeriodField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  schemeType: SchemeTypes
) => {
  const monthQuarters = VatReturnListSelectors.useGetMonthQuarterList();
  const periodQuarters = VatReturnListSelectors.useGetQuarters();
  let quarters = null;
  if (schemeType === SchemeTypes.IOSS) {
    quarters = monthQuarters;
  } else if (
    schemeType === SchemeTypes.EU ||
    schemeType === SchemeTypes.NON_EU
  ) {
    quarters = periodQuarters;
  }

  let quarters_options = [];
  quarters_options.push({
    id: null,
    label: Translations.period_control.English,
  });
  if (quarters && quarters.status === StateStatus.Success)
    for (let quarter of quarters.data)
      quarters_options.push({
        id: quarter.quarter_id,
        label: quarter.short_text,
      });
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="period"
      label={<TranslatedText textMap={Translations.period_control} />}
      error={'period' in errors}
      name="period"
      control={control}
      rules={{ required: 'Required Field' }}
      options={quarters_options as any}
      helperText={errors.period && errors.period.message}
    />
  );
};

const renderYearField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const startYear = 2021;
  const years_options = [];
  years_options.push({
    id: null,
    label: Translations.year_control.English,
  });
  for (let year = startYear; year <= currentYear; year++) {
    var obj = {
      id: `${year}`,
      label: `${year}`,
    };
    years_options.push(obj);
  }

  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="year"
      label={<TranslatedText textMap={Translations.year_control} />}
      error={'year' in errors}
      name="year"
      control={control}
      rules={{ required: 'Required Field' }}
      options={years_options as any}
      helperText={errors.year && errors.year.message}
    />
  );
};

const renderStartDateField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  watchStartDate: Date | null,
  watchEndDate: Date | null,
  watchYear: string | null,
  watchQuarter: string | null
) => {
  let quarter = watchQuarter
    ? findQuarterById(Number.parseInt(watchQuarter))
    : null;
  return (
    <AppDatePicker
      fullWidth
      margin="normal"
      label={<TranslatedText textMap={Translations.start_date_control} />}
      autoOk
      error={'startDate' in errors}
      control={control}
      rules={{
        required: watchEndDate ? 'Required Field' : false,
        validate: () => {
          if (
            watchStartDate &&
            quarter &&
            watchYear &&
            !DateInMonthsRange(
              watchStartDate,
              quarter.starting_month,
              quarter.ending_month,
              watchYear
            )
          ) {
            return 'Starting date should be in selected quarter range';
          }

          if (
            (watchStartDate || watchEndDate) &&
            !hasSelectedPeriodEnded(watchQuarter, watchYear)
          ) {
            return 'The selected period has not ended yet.';
          }

          return true;
        },
      }}
      name="startDate"
      helperText={errors.startDate && errors.startDate.message}
    />
  );
};

const renderNotes = () => {
  return (
    <div>
      <div>
        <strong>
          <TranslatedText textMap={Translations.note1} />
        </strong>
      </div>
      <div>
        <TranslatedText textMap={Translations.note2} />
      </div>
    </div>
  );
};

const renderEndDateField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  watchStartDate: Date | null,
  watchEndDate: Date | null,
  watchYear: string | null,
  watchQuarter: string | null
) => {
  let quarter = watchQuarter
    ? findQuarterById(Number.parseInt(watchQuarter))
    : null;
  return (
    <AppDatePicker
      fullWidth
      margin="normal"
      label={<TranslatedText textMap={Translations.end_date_control} />}
      autoOk
      error={'endDate' in errors}
      control={control}
      rules={{
        required: watchStartDate ? 'Required Field' : false,
        validate: () => {
          if (
            watchEndDate &&
            quarter &&
            watchYear &&
            !DateInMonthsRange(
              watchEndDate,
              quarter.starting_month,
              quarter.ending_month,
              watchYear
            )
          ) {
            return 'Ending date should be in selected quarter range';
          }

          if (
            (watchStartDate || watchEndDate) &&
            !hasSelectedPeriodEnded(watchQuarter, watchYear)
          ) {
            return 'The selected period has not ended yet.';
          }

          return true;
        },
      }}
      name="endDate"
      helperText={errors.endDate && errors.endDate.message}
    />
  );
};

const renderActiveRegistrationError = () => {
  return (
    <span style={{ color: 'firebrick' }}>
      {Translations.active_registration_error.English}
    </span>
  );
};

const findQuarterById = (id: number) => {
  const state = getState(ReducerKeys.VATRETURN_LIST_REDUCER);
  const types = [
    ...(state.quarterList.data ?? []),
    ...(state.monthQuarterList.data ?? []),
  ];

  for (let type of types) if (type.quarter_id === id) return type;
};

const hasSelectedPeriodEnded = (
  watchQuarter: string | null,
  watchYear: string | null
) => {
  if (!watchQuarter || !watchYear) return true;

  const quarter = findQuarterById(Number.parseInt(watchQuarter));
  if (!quarter) return true;

  const periodEndDate = new Date(
    Number.parseInt(watchYear),
    quarter.ending_month,
    0,
    23,
    59,
    59,
    999
  );

  return new Date() > periodEndDate;
};
